/** @module IglooAuctioneer **/

/**
 * Export the IglooAuctioneer module
 * @param  {Igloo} igloo The igloo module
 * @return {Object}      The public functions:
 * [requestBid]{@link module:IglooAuctioneer~request_bid} and
 * [addUnit]{@link module:IglooAuctioneer~add_unit}
 */
module.exports = function(igloo) {
  /**
   * A map of the registered ad units
   * @type {Object}
   */
  var ad_units = {};
  /**
   * A map of the functions to call for each ad unit when the auction is
   * complete
   * @type {Object}
   */
  var callback_functions = {};
  /**
   * A map of the ad units that has the time that an auction is required for
   * each
   * @type {Object}
   */
  var bids_required = {};
  /**
   * Igloo Logger
   * @type {module:IglooLogger}
   */
  var log = require('./IglooLogger')(igloo, 'PREDICT Auctioneer');
  /**
   * The timeout (in ms) for each auction
   * @type {Number}
   */
  var auction_timeout = 1000;
  /**
   * How frequently (in ms) to check for ad units that need auctions run. This
   * is now handled in IglooTimer and is hard-coded at 100ms. This variable is
   * still used in request_bid so is left here
   * @type {Number}
   */
  var auction_interval = 100;

  /**
   * A map that prevents an auction for running at the same time for a given unit.
   * @type {Object}
   */
  var running_auction = {};

  /**
   * This function sets the bids_required value for the given ad unit to now
   * @param  {String} name The name of the ad unit to request a bid for
   * @return {Boolean}     Whether the function succeeded or failed
   */
  function request_bid(name) {
    log.trace('request_bid', arguments);

    // Check for a valid AU
    if (typeof bids_required[name] === 'undefined') {
      log.warn('Invalid Bid Request For', name);
      return false;
    }

    // Set the bid required
    var current_time = (new Date()).getTime();
    bids_required[name] = Math.floor((current_time + auction_timeout) / auction_interval) * auction_interval;
    return true;
  } // --> request_bid

  /**
   * Add an ad unit to the array of ad units that this module runs auctions for
   * @param {String}   name     The name of the ad unit. This takes the form of
   *                            `igloo_#`
   * @param {Object}   config   The coniguration for the ad unit to pass into
   *                            prebid
   * @param {Function} callback The function to call when an auction is complete
   *                            for this ad unit
   * @param {Object}   pbjs     The prebid instance to add the unit to
   */
  function add_unit(name, config, callback, pbjs) {
    log.trace('add_unit', arguments);

    // Add to the arrays
    bids_required[name] = null;
    callback_functions[name] = callback;
    ad_units[name] = {
      name: name,
      pbjs: pbjs,
    };
    // set to bot to prevent auctions from running on bot traffic
    // if client is not a bot then this will be set to false and the auction can run normally
    running_auction[name] = igloo.browser.bot;
    
    // Add to prebid
    pbjs.addAdUnits(config);

    log.info(name, 'Registered Bidder');
  } // --> add_unit

  /**
   * This function loops over the ad units and runs auctions for any ad units
   * that need them run. It is run on an interval
   */
  function run_auctions() {
    // determine if all units are running an auction, if so then exit
    var allRunning = Object.values(running_auction).filter(function (value) {
      return value === false;
    }).length === 0;
    if (allRunning) {
      return false;
    }

    // Check for ad units that need auctions run
    var need_auctions = [];
    var auction_to_run = false;
    var current_time = (new Date()).getTime();
    Object.values(ad_units).forEach(function(item) {
      // Check for null (if in the future still run the auction)
      if (bids_required[item.name] === null) return true;

      // If bid required time is in the past, add to array
      if (bids_required[item.name] <= current_time) {
        // Add to the array
        need_auctions.push(item);
        // set flag to true to run actions
        auction_to_run = true;
      }
    });

    // Exit if no auctions need to be run
    if (!auction_to_run) {
      return true;
    }

    var need_auctions_names = need_auctions.map(function(item) {
      return item.name;
    });

    // Reset the auctions
    clear_auctions(need_auctions_names, bids_required);

    need_auctions.forEach(function(item) {
      // Set the flag to prevent auctions for this unit
      running_auction[item.name] = true;
      log.info('Running auction for:', item.name);

      // Set up the callback function and force it to run after a period
      var auction_callback_bound = auction_callback.bind(this, item.name, callback_functions[item.name], {
        do: true
      });
      setTimeout(auction_callback_bound, auction_timeout * 2);

      // Run the auction
      item.pbjs.requestBids({
        adUnitCodes: [item.name],
        bidsBackHandler: auction_callback_bound,
        timeout: auction_timeout,
      });
    });
  } // --> run_auctions

  /**
   * Clear the old bids from prebid and reset the timing
   * @param  {Array}   unit_names   The ad units to reset
   * @param  {Object}  unit_objects A copy of the local variable `bids_required`
   * @return {Integer}              The number of items cleared
   */
  function clear_auctions(unit_names, unit_objects) {
    log.trace('clear_auctions', arguments);

    // The number of cleared auctions
    var cleared_auctions = 0;

    for (var i = 0; i < unit_names.length; i++) {
      // Check for existence
      if (typeof unit_objects[unit_names[i]] === 'undefined') {
        continue;
      }

      // Reset the unit_objects
      unit_objects[unit_names[i]] = null;

      // Increment the counter
      cleared_auctions++;
    }

    return cleared_auctions;
  } // --> clear_auctions

  /**
   * Callback to be ran at the completion of the auction
   * @param  {Array}   ad_unit_name       The ad unit name that had the auction run
   * @param  {Array}   callback_function  The function to call when an auction
   *                                      is run
   * @param  {Object}  control_variable   A object with the key do to prevent
   *                                      running multiple times
   * @return {Boolean}                    Whether the callbacks were run
   */
  function auction_callback(ad_unit_name, callback_function, control_variable) {
    log.trace('auction_callback', arguments);

    // Check the control variable for the auction having run
    if (!control_variable.do) {
      return false;
    }
    control_variable.do = false;

    // Call the needed callback function
    callback_function(ad_unit_name);

    // Allow the next auctions of unit to proceed
    running_auction[ad_unit_name] = false;

    return true;
  } // --> auction_callback

  igloo.timer.registerFunction(run_auctions);

  return {
    // Needed for external calls
    requestBid: request_bid,
    addUnit: add_unit,

    // Exported for testing
    clear_auctions: clear_auctions,
    auction_callback: auction_callback,
  };
}; // --> module.exports
